import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell, Field, inputClass } from "@/components/AuthShell";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success(data.message || "Password reset. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid Link" subtitle="This reset link is missing or malformed"
        footer={<Link to="/login" className="text-amber-300 hover:text-amber-200">Back to Sign In</Link>}>
        <div className="text-center" data-testid="reset-invalid">
          <p className="text-sm text-slate-300">Please request a new password reset link.</p>
          <Link to="/forgot-password" className="btn-gold rounded-full px-6 py-2.5 text-sm mt-5 inline-block">
            Request New Link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset Password" subtitle="Choose a new password for your account"
      footer={<Link to="/login" className="text-amber-300 hover:text-amber-200">Back to Sign In</Link>}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <Field label="New Password">
          <input data-testid="reset-password-input" type="password" required value={pw}
            onChange={(e) => setPw(e.target.value)} className={inputClass} placeholder="At least 6 characters" />
        </Field>
        <Field label="Confirm Password">
          <input data-testid="reset-confirm-input" type="password" required value={confirm}
            onChange={(e) => setConfirm(e.target.value)} className={inputClass} placeholder="Re-enter new password" />
        </Field>
        <button data-testid="reset-submit-button" type="submit" disabled={loading}
          className="btn-gold rounded-full w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60">
          <KeyRound size={18} /> {loading ? "Resetting…" : "Reset Password"}
        </button>
      </form>
    </AuthShell>
  );
}
